import { useColorModeValue } from '@ecoinvent/ui';
import { Handle, NodeProps, Position } from '@xyflow/react';
import { FiArrowLeft, FiArrowRight, FiMinus, FiPlus } from 'react-icons/fi';

import { SupplyChainNode as SupplyChainNodeData } from 'hooks/data/datasets/V4/supplyChain/types';

import { COLORS } from './constants';
import { ImpactScoreState } from './ImpactScore';
import SupplyChainCard, { useCardAccent } from './SupplyChainCard';
import { stopEvents } from './utils';

export type NodeVariant = 'root' | 'input' | 'consumer';

export type SupplyChainNodePayload = {
  node: SupplyChainNodeData;
  nodeVariant: NodeVariant;
  isRoot: boolean;
  isActive: boolean;
  isFunctional: boolean;
  isExpanded: boolean;
  isOutputsExpanded: boolean;
  inputCount?: number;
  outputCount?: number;
  mode: 'flows' | 'impact_assessment';
  nodeHeight: number;
  scoreStatus: ImpactScoreState['status'];
  score?: number;
  scoreUnit?: string;
  onExpand: () => void;
  onCollapse: () => void;
  onExpandOutputs: () => void;
  onCollapseOutputs: () => void;
  onSelect: () => void;
  onViewDataset: () => void;
  onCopyScore: () => Promise<void>;
};

const invisibleHandle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  width: 1,
  height: 1,
  top: '50%',
};

const badgeAnchorStyle = (side: 'left' | 'right'): React.CSSProperties => ({
  position: 'absolute',
  [side]: 0,
  top: '50%',
  transform: `translate(${side === 'left' ? '-50%' : '50%'}, -50%)`,
  display: 'inline-flex',
  zIndex: 10,
});

const badgeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

const BADGE_ICON_SIZE = 14;

const badgeIconStyle: React.CSSProperties = { display: 'block', flexShrink: 0 };

const badgePillStyle = (backgroundColor: string, color: string): React.CSSProperties => ({
  backgroundColor,
  color,
  borderRadius: '9999px',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'inherit',
  lineHeight: 1,
  padding: '0 8px',
  height: '24px',
  minWidth: '33px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
});

const pillLabel = (isExpanded: boolean, count: number | undefined, noun: 'input' | 'output'): string => {
  const action = isExpanded ? 'Collapse' : 'Expand';
  const subject = count === 1 ? noun : `${noun}s`;
  return count === undefined ? `${action} ${subject}` : `${action} ${count} ${subject}`;
};

const FLOW_BADGE_SIZE = 24;
const FLOW_BADGE_INNER_SIZE = 18.29;
const FLOW_BADGE_GAP = 4;
const FLOW_ARROW_SIZE = 10;

const FLOW_BADGE_SHADOW = '0 1.5px 3px -0.75px rgba(0,0,0,0.06), 0 3px 4.5px -0.75px rgba(0,0,0,0.10)';

const flowBadgeStyle = (side: 'left' | 'right', outerColor: string): React.CSSProperties => ({
  position: 'absolute',
  top: '50%',
  [side === 'right' ? 'left' : 'right']: '100%',
  [side === 'right' ? 'marginLeft' : 'marginRight']: FLOW_BADGE_GAP,
  transform: 'translateY(-50%)',
  width: FLOW_BADGE_SIZE,
  height: FLOW_BADGE_SIZE,
  borderRadius: '9999px',
  backgroundColor: outerColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  boxShadow: FLOW_BADGE_SHADOW,
});

const flowBadgeInnerStyle = (innerColor: string, glyphColor: string): React.CSSProperties => ({
  width: FLOW_BADGE_INNER_SIZE,
  height: FLOW_BADGE_INNER_SIZE,
  borderRadius: '9999px',
  backgroundColor: innerColor,
  color: glyphColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

type FlowDirectionBadgeProps = {
  side: 'left' | 'right';
  outerColor: string;
  innerColor: string;
  glyphColor: string;
  isFunctional?: boolean;
};

const BadgeIcon = ({ isExpanded }: { isExpanded: boolean }) =>
  isExpanded ? (
    <FiMinus aria-hidden size={BADGE_ICON_SIZE} style={badgeIconStyle} />
  ) : (
    <FiPlus aria-hidden size={BADGE_ICON_SIZE} style={badgeIconStyle} />
  );

const FlowDirectionBadge = ({ side, outerColor, innerColor, glyphColor, isFunctional }: FlowDirectionBadgeProps) => {
  const pointsRight = (side === 'right') !== Boolean(isFunctional);
  const Arrow = pointsRight ? FiArrowRight : FiArrowLeft;
  return (
    <span aria-hidden style={flowBadgeStyle(side, outerColor)}>
      <div style={flowBadgeInnerStyle(innerColor, glyphColor)}>
        <Arrow size={FLOW_ARROW_SIZE} style={badgeIconStyle} />
      </div>
    </span>
  );
};

const SupplyChainNode = ({ data }: NodeProps) => {
  const {
    node,
    nodeVariant,
    isRoot,
    isActive,
    isFunctional,
    isExpanded,
    isOutputsExpanded,
    inputCount,
    outputCount,
    mode,
    nodeHeight,
    scoreStatus,
    score,
    scoreUnit,
    onExpand,
    onCollapse,
    onExpandOutputs,
    onCollapseOutputs,
    onSelect,
    onViewDataset,
    onCopyScore,
  } = data as SupplyChainNodePayload;

  const expandBg = useColorModeValue(COLORS.expandBgLight, COLORS.expandBgDark);
  const expandColor = useColorModeValue(COLORS.expandGreenLight, COLORS.expandGreenDark);
  const collapseBg = useColorModeValue(COLORS.collapseBgLight, COLORS.collapseBgDark);
  const collapseColor = COLORS.collapseRed;
  const { flowColor, isFunctionalAccent } = useCardAccent({ nodeVariant, isRoot, isActive, isFunctional, mode });
  const flowOuterColor = useColorModeValue(COLORS.expandBgLight, COLORS.badgeOuterDark);
  const functionalArrowColor = useColorModeValue('#FFFFFF', COLORS.badgeOuterDark);
  const flowGlyphColor = isFunctionalAccent ? functionalArrowColor : '#FFFFFF';

  const hasInputs = inputCount === undefined || inputCount > 0;
  const hasOutputs = outputCount === undefined || outputCount > 0;
  const flowBadgeSide = isRoot || mode !== 'impact_assessment' ? undefined : nodeVariant === 'input' ? 'right' : 'left';
  const flowBadge = flowBadgeSide && (
    <FlowDirectionBadge
      side={flowBadgeSide}
      outerColor={flowOuterColor}
      innerColor={flowColor}
      glyphColor={flowGlyphColor}
      isFunctional={isFunctional}
    />
  );

  return (
    <div className="nopan nodrag" style={{ position: 'relative', cursor: 'default' }}>
      <Handle type="target" position={Position.Left} style={invisibleHandle} />

      {(hasInputs || flowBadgeSide === 'left') && (
        <div style={badgeAnchorStyle('left')}>
          {hasInputs && (
            <button
              type="button"
              aria-label={pillLabel(isExpanded, inputCount, 'input')}
              aria-expanded={isExpanded}
              className="nopan nodrag"
              style={badgeButtonStyle}
              onPointerDown={stopEvents}
              onMouseDown={stopEvents}
              onClick={(e) => {
                stopEvents(e);
                isExpanded ? onCollapse() : onExpand();
              }}
            >
              <span style={badgePillStyle(isExpanded ? collapseBg : expandBg, isExpanded ? collapseColor : expandColor)}>
                <BadgeIcon isExpanded={isExpanded} />
                {inputCount}
              </span>
            </button>
          )}
          {flowBadgeSide === 'left' && flowBadge}
        </div>
      )}

      <SupplyChainCard
        node={node}
        nodeVariant={nodeVariant}
        isRoot={isRoot}
        isActive={isActive}
        isFunctional={isFunctional}
        mode={mode}
        nodeHeight={nodeHeight}
        scoreStatus={scoreStatus}
        score={score}
        scoreUnit={scoreUnit}
        onSelect={onSelect}
        onViewDataset={onViewDataset}
        onCopyScore={onCopyScore}
      />

      {(hasOutputs || flowBadgeSide === 'right') && (
        <div style={badgeAnchorStyle('right')}>
          {hasOutputs && (
            <button
              type="button"
              aria-label={pillLabel(isOutputsExpanded, outputCount, 'output')}
              aria-expanded={isOutputsExpanded}
              className="nopan nodrag"
              style={badgeButtonStyle}
              onPointerDown={stopEvents}
              onMouseDown={stopEvents}
              onClick={(e) => {
                stopEvents(e);
                isOutputsExpanded ? onCollapseOutputs() : onExpandOutputs();
              }}
            >
              <span
                style={badgePillStyle(
                  isOutputsExpanded ? collapseBg : expandBg,
                  isOutputsExpanded ? collapseColor : expandColor
                )}
              >
                <BadgeIcon isExpanded={isOutputsExpanded} />
                {outputCount}
              </span>
            </button>
          )}
          {flowBadgeSide === 'right' && flowBadge}
        </div>
      )}

      <Handle type="source" position={Position.Right} style={invisibleHandle} />
    </div>
  );
};

export default SupplyChainNode;
